import { useState, useEffect, useCallback, useRef } from 'react'
import { useAuthStore } from '../store/authStore'
import { passwordRuleProblem } from '../services/passwordRule'
import { Leaf, Eye, EyeOff, AlertCircle, Loader2, ArrowLeft, ShieldCheck, KeyRound, Lock, CheckCircle2 } from 'lucide-react'

export default function LoginPage() {
  const {
    adminLogin,
    verifyAdminOtp,
    resendAdminOtp,
    clearOtpState,
    forgotPassword,
    verifyResetOtp,
    resetPassword,
    clearResetState,
    isLoading,
    error,
    clearError,
    otpPendingToken,
    otpEmail,
    resetPhase,
    resetEmail,
  } = useAuthStore()

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [otpCode, setOtpCode] = useState('')
  const [resendCountdown, setResendCountdown] = useState(0)
  const otpInputRef = useRef<HTMLInputElement>(null)

  // Reset password phase state
  const [resetEmailInput, setResetEmailInput] = useState('')
  const [resetOtpCode, setResetOtpCode] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [showNewPassword, setShowNewPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [passwordError, setPasswordError] = useState('')
  const resetOtpInputRef = useRef<HTMLInputElement>(null)

  const isOtpPhase = !!otpPendingToken && resetPhase === 'idle'

  // Focus OTP input when entering OTP phase
  useEffect(() => {
    if (isOtpPhase && otpInputRef.current) {
      otpInputRef.current.focus()
    }
  }, [isOtpPhase])

  // Focus reset OTP input
  useEffect(() => {
    if (resetPhase === 'otp' && resetOtpInputRef.current) {
      resetOtpInputRef.current.focus()
    }
  }, [resetPhase])

  // Resend countdown timer
  useEffect(() => {
    if (resendCountdown <= 0) return
    const timer = setInterval(() => {
      setResendCountdown((prev) => prev - 1)
    }, 1000)
    return () => clearInterval(timer)
  }, [resendCountdown])

  async function handleLoginSubmit(e: React.FormEvent) {
    e.preventDefault()
    clearError()
    const result = await adminLogin(email, password)
    if (result && result.requiresOtp) {
      setOtpCode('')
      setResendCountdown(60)
    }
  }

  async function handleOtpSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (otpCode.length !== 6) return
    clearError()
    await verifyAdminOtp(otpCode)
  }

  const handleResendOtp = useCallback(async () => {
    if (resendCountdown > 0) return
    const success = await resendAdminOtp()
    if (success) {
      setResendCountdown(60)
      setOtpCode('')
    }
  }, [resendCountdown, resendAdminOtp])

  function handleBackToLogin() {
    clearOtpState()
    clearResetState()
    setOtpCode('')
    setResetOtpCode('')
    setResetEmailInput('')
    setNewPassword('')
    setConfirmPassword('')
    setPasswordError('')
    setResendCountdown(0)
  }

  function handleOtpInputChange(value: string) {
    const sanitized = value.replace(/\D/g, '').slice(0, 6)
    setOtpCode(sanitized)
  }

  function handleResetOtpInputChange(value: string) {
    const sanitized = value.replace(/\D/g, '').slice(0, 6)
    setResetOtpCode(sanitized)
  }

  // Forgot password handlers
  function handleForgotPasswordClick() {
    clearError()
    setResetEmailInput(email) // Pre-fill with login email if entered
    useAuthStore.setState({ resetPhase: 'forgot' })
  }

  async function handleForgotSubmit(e: React.FormEvent) {
    e.preventDefault()
    clearError()
    const success = await forgotPassword(resetEmailInput)
    if (success) {
      setResetOtpCode('')
      setResendCountdown(60)
    }
  }

  async function handleResetOtpSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (resetOtpCode.length !== 6) return
    clearError()
    await verifyResetOtp(resetOtpCode)
  }

  async function handleResendResetOtp() {
    if (resendCountdown > 0 || !resetEmail) return
    clearError()
    const success = await forgotPassword(resetEmail)
    if (success) {
      setResendCountdown(60)
      setResetOtpCode('')
    }
  }

  async function handleResetPasswordSubmit(e: React.FormEvent) {
    e.preventDefault()
    setPasswordError('')
    clearError()

    const problem = passwordRuleProblem(newPassword)
    if (problem) {
      setPasswordError(problem)
      return
    }
    if (newPassword !== confirmPassword) {
      setPasswordError('Passwords do not match.')
      return
    }

    const success = await resetPassword(newPassword)
    if (success) {
      setNewPassword('')
      setConfirmPassword('')
    }
  }

  // Determine header icon and subtitle
  function getHeaderContent() {
    if (resetPhase === 'forgot') return { icon: <KeyRound className="w-8 h-8 text-white" />, subtitle: 'Password Recovery' }
    if (resetPhase === 'otp') return { icon: <ShieldCheck className="w-8 h-8 text-white" />, subtitle: 'Verify Reset Code' }
    if (resetPhase === 'newPassword') return { icon: <Lock className="w-8 h-8 text-white" />, subtitle: 'Create New Password' }
    if (isOtpPhase) return { icon: <ShieldCheck className="w-8 h-8 text-white" />, subtitle: 'Two-Factor Authentication' }
    return { icon: <Leaf className="w-8 h-8 text-white" />, subtitle: 'Admin Console' }
  }

  const { icon: headerIcon, subtitle: headerSubtitle } = getHeaderContent()

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-600 via-primary-700 to-[#1B5E20] flex items-center justify-center p-4">
      {/* Background pattern */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-96 h-96 bg-white/5 rounded-full" />
        <div className="absolute -bottom-40 -left-40 w-96 h-96 bg-white/5 rounded-full" />
        <div className="absolute top-1/2 left-1/4 w-64 h-64 bg-white/3 rounded-full" />
      </div>

      <div className="relative w-full max-w-md">
        {/* Card */}
        <div className="bg-white rounded-3xl shadow-2xl overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-primary-600 to-primary-500 px-8 py-8 text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-white/20 mb-4">
              {headerIcon}
            </div>
            <h1 className="text-2xl font-bold text-white">SmartWasteCycle</h1>
            <p className="text-primary-100 text-sm mt-1 font-medium">{headerSubtitle}</p>
          </div>

          {/* Form area */}
          <div className="px-8 py-8">
            {(error || passwordError) && (
              <div className="flex items-start gap-2.5 p-3 bg-red-50 border border-red-200 rounded-xl mb-5">
                <AlertCircle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
                <p className="text-sm text-red-600">{passwordError || error}</p>
              </div>
            )}

            {resetPhase === 'forgot' ? (
              /* ======================== Forgot Password: Email Input ======================== */
              <>
                <div className="flex items-center gap-3 mb-1">
                  <button
                    type="button"
                    onClick={handleBackToLogin}
                    className="p-1.5 -ml-1.5 rounded-lg text-[#6B7280] hover:text-[#1A1A2E] hover:bg-gray-100 transition-colors"
                    aria-label="Back to sign in"
                  >
                    <ArrowLeft className="w-4 h-4" />
                  </button>
                  <h2 className="text-xl font-bold text-[#1A1A2E]">Reset your password</h2>
                </div>
                <p className="text-sm text-[#6B7280] mb-6">
                  Enter your admin email address and we'll send you a verification code.
                </p>

                <form onSubmit={handleForgotSubmit} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-[#1A1A2E] mb-1.5">
                      Email Address
                    </label>
                    <input
                      type="email"
                      value={resetEmailInput}
                      onChange={(e) => setResetEmailInput(e.target.value)}
                      placeholder="admin@smartwastecycle.org"
                      required
                      autoFocus
                      className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm text-[#1A1A2E] placeholder-[#6B7280] bg-[#F8FAF9] focus:outline-none focus:ring-2 focus:ring-primary-600/30 focus:border-primary-600 transition-colors"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={isLoading}
                    className="w-full py-3 bg-primary-600 hover:bg-primary-700 text-white font-semibold rounded-xl transition-colors flex items-center justify-center gap-2 mt-2 disabled:opacity-70 disabled:cursor-not-allowed"
                  >
                    {isLoading ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Sending code...
                      </>
                    ) : (
                      'Send Reset Code'
                    )}
                  </button>
                </form>

                <div className="mt-5 text-center">
                  <button
                    type="button"
                    onClick={handleBackToLogin}
                    className="text-sm font-medium text-primary-600 hover:text-primary-700 transition-colors"
                  >
                    Back to Sign In
                  </button>
                </div>
              </>

            ) : resetPhase === 'otp' ? (
              /* ======================== Forgot Password: OTP Verification ======================== */
              <>
                <div className="flex items-center gap-3 mb-1">
                  <button
                    type="button"
                    onClick={handleBackToLogin}
                    className="p-1.5 -ml-1.5 rounded-lg text-[#6B7280] hover:text-[#1A1A2E] hover:bg-gray-100 transition-colors"
                    aria-label="Back to sign in"
                  >
                    <ArrowLeft className="w-4 h-4" />
                  </button>
                  <h2 className="text-xl font-bold text-[#1A1A2E]">Check your email</h2>
                </div>
                <p className="text-sm text-[#6B7280] mb-6">
                  Enter the 6-digit code sent to{' '}
                  <span className="font-medium text-[#1A1A2E]">{resetEmail}</span>
                </p>

                <form onSubmit={handleResetOtpSubmit} className="space-y-5">
                  <div>
                    <label className="block text-sm font-medium text-[#1A1A2E] mb-1.5">
                      Verification Code
                    </label>
                    <input
                      ref={resetOtpInputRef}
                      type="text"
                      inputMode="numeric"
                      maxLength={6}
                      value={resetOtpCode}
                      onChange={(e) => handleResetOtpInputChange(e.target.value)}
                      placeholder="000000"
                      required
                      autoComplete="one-time-code"
                      className="w-full px-4 py-3.5 rounded-xl border border-gray-200 text-center text-2xl font-mono tracking-[0.5em] text-[#1A1A2E] placeholder-[#D1D5DB] bg-[#F8FAF9] focus:outline-none focus:ring-2 focus:ring-primary-600/30 focus:border-primary-600 transition-colors"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={isLoading || resetOtpCode.length !== 6}
                    className="w-full py-3 bg-primary-600 hover:bg-primary-700 text-white font-semibold rounded-xl transition-colors flex items-center justify-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed"
                  >
                    {isLoading ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Verifying...
                      </>
                    ) : (
                      'Verify Code'
                    )}
                  </button>
                </form>

                <div className="mt-5 text-center">
                  {resendCountdown > 0 ? (
                    <p className="text-sm text-[#6B7280]">
                      Resend code in{' '}
                      <span className="font-medium text-[#1A1A2E]">{resendCountdown}s</span>
                    </p>
                  ) : (
                    <button
                      type="button"
                      onClick={handleResendResetOtp}
                      className="text-sm font-medium text-primary-600 hover:text-primary-700 transition-colors"
                    >
                      Resend verification code
                    </button>
                  )}
                </div>

                <div className="mt-3 text-center">
                  <button
                    type="button"
                    onClick={handleBackToLogin}
                    className="text-sm text-[#6B7280] hover:text-[#1A1A2E] transition-colors"
                  >
                    Back to Sign In
                  </button>
                </div>
              </>

            ) : resetPhase === 'newPassword' ? (
              /* ======================== Forgot Password: New Password ======================== */
              <>
                <div className="flex items-center gap-3 mb-1">
                  <button
                    type="button"
                    onClick={handleBackToLogin}
                    className="p-1.5 -ml-1.5 rounded-lg text-[#6B7280] hover:text-[#1A1A2E] hover:bg-gray-100 transition-colors"
                    aria-label="Back to sign in"
                  >
                    <ArrowLeft className="w-4 h-4" />
                  </button>
                  <h2 className="text-xl font-bold text-[#1A1A2E]">Create new password</h2>
                </div>
                <p className="text-sm text-[#6B7280] mb-6">
                  Choose a strong password for your admin account.
                </p>

                <form onSubmit={handleResetPasswordSubmit} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-[#1A1A2E] mb-1.5">
                      New Password
                    </label>
                    <div className="relative">
                      <input
                        type={showNewPassword ? 'text' : 'password'}
                        value={newPassword}
                        onChange={(e) => { setNewPassword(e.target.value); setPasswordError('') }}
                        placeholder="Enter new password"
                        required
                        autoFocus
                        className="w-full px-4 py-3 pr-11 rounded-xl border border-gray-200 text-sm text-[#1A1A2E] placeholder-[#6B7280] bg-[#F8FAF9] focus:outline-none focus:ring-2 focus:ring-primary-600/30 focus:border-primary-600 transition-colors"
                      />
                      <button
                        type="button"
                        onClick={() => setShowNewPassword(!showNewPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-[#6B7280] hover:text-[#1A1A2E] transition-colors"
                      >
                        {showNewPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                      </button>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-[#1A1A2E] mb-1.5">
                      Confirm Password
                    </label>
                    <div className="relative">
                      <input
                        type={showConfirmPassword ? 'text' : 'password'}
                        value={confirmPassword}
                        onChange={(e) => { setConfirmPassword(e.target.value); setPasswordError('') }}
                        placeholder="Confirm new password"
                        required
                        className="w-full px-4 py-3 pr-11 rounded-xl border border-gray-200 text-sm text-[#1A1A2E] placeholder-[#6B7280] bg-[#F8FAF9] focus:outline-none focus:ring-2 focus:ring-primary-600/30 focus:border-primary-600 transition-colors"
                      />
                      <button
                        type="button"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-[#6B7280] hover:text-[#1A1A2E] transition-colors"
                      >
                        {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                      </button>
                    </div>
                  </div>

                  {/* Password requirements */}
                  <div className="p-3 bg-[#F8FAF9] rounded-xl border border-gray-100">
                    <p className="text-xs font-medium text-[#6B7280] mb-2">Password requirements:</p>
                    <div className="space-y-1">
                      <RequirementItem met={newPassword.length >= 8} text="At least 8 characters" />
                      <RequirementItem met={/[A-Z]/.test(newPassword)} text="1 uppercase letter" />
                      <RequirementItem met={/\d/.test(newPassword)} text="1 number" />
                      <RequirementItem met={newPassword.length > 0 && newPassword === confirmPassword} text="Passwords match" />
                    </div>
                  </div>

                  <button
                    type="submit"
                    disabled={isLoading}
                    className="w-full py-3 bg-primary-600 hover:bg-primary-700 text-white font-semibold rounded-xl transition-colors flex items-center justify-center gap-2 mt-2 disabled:opacity-70 disabled:cursor-not-allowed"
                  >
                    {isLoading ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Resetting password...
                      </>
                    ) : (
                      'Reset Password'
                    )}
                  </button>
                </form>

                <div className="mt-5 text-center">
                  <button
                    type="button"
                    onClick={handleBackToLogin}
                    className="text-sm text-[#6B7280] hover:text-[#1A1A2E] transition-colors"
                  >
                    Back to Sign In
                  </button>
                </div>
              </>

            ) : !isOtpPhase ? (
              /* ======================== Phase 1: Email + Password ======================== */
              <>
                <h2 className="text-xl font-bold text-[#1A1A2E] mb-1">Welcome back</h2>
                <p className="text-sm text-[#6B7280] mb-6">Sign in to your admin account</p>

                <form onSubmit={handleLoginSubmit} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-[#1A1A2E] mb-1.5">
                      Email Address
                    </label>
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="admin@smartwastecycle.org"
                      required
                      className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm text-[#1A1A2E] placeholder-[#6B7280] bg-[#F8FAF9] focus:outline-none focus:ring-2 focus:ring-primary-600/30 focus:border-primary-600 transition-colors"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-[#1A1A2E] mb-1.5">
                      Password
                    </label>
                    <div className="relative">
                      <input
                        type={showPassword ? 'text' : 'password'}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder="Enter your password"
                        required
                        className="w-full px-4 py-3 pr-11 rounded-xl border border-gray-200 text-sm text-[#1A1A2E] placeholder-[#6B7280] bg-[#F8FAF9] focus:outline-none focus:ring-2 focus:ring-primary-600/30 focus:border-primary-600 transition-colors"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-[#6B7280] hover:text-[#1A1A2E] transition-colors"
                      >
                        {showPassword ? <EyeOff className="w-4.5 h-4.5" size={18} /> : <Eye className="w-4.5 h-4.5" size={18} />}
                      </button>
                    </div>
                  </div>

                  <button
                    type="submit"
                    disabled={isLoading}
                    className="w-full py-3 bg-primary-600 hover:bg-primary-700 text-white font-semibold rounded-xl transition-colors flex items-center justify-center gap-2 mt-2 disabled:opacity-70 disabled:cursor-not-allowed"
                  >
                    {isLoading ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Signing in...
                      </>
                    ) : (
                      'Sign In'
                    )}
                  </button>
                </form>

                {/* Forgot Password link */}
                <div className="mt-4 text-center">
                  <button
                    type="button"
                    onClick={handleForgotPasswordClick}
                    className="text-sm font-medium text-primary-600 hover:text-primary-700 transition-colors"
                  >
                    Forgot Password?
                  </button>
                </div>

                {/* Demo credentials -- dev only */}
                {import.meta.env.DEV && (
                  <div className="mt-6 p-4 bg-primary-50 rounded-xl border border-primary-100">
                    <p className="text-xs font-semibold text-primary-700 mb-2">Demo Credentials</p>
                    <div className="space-y-1">
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-[#6B7280] w-16">Email:</span>
                        <code className="text-xs font-mono text-primary-700 bg-white px-2 py-0.5 rounded border border-primary-100">
                          admin@smartwastecycle.gh
                        </code>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-[#6B7280] w-16">Password:</span>
                        <code className="text-xs font-mono text-primary-700 bg-white px-2 py-0.5 rounded border border-primary-100">
                          password123
                        </code>
                      </div>
                    </div>
                  </div>
                )}
              </>
            ) : (
              /* ======================== Phase 2: Login OTP Verification ======================== */
              <>
                <div className="flex items-center gap-3 mb-1">
                  <button
                    type="button"
                    onClick={handleBackToLogin}
                    className="p-1.5 -ml-1.5 rounded-lg text-[#6B7280] hover:text-[#1A1A2E] hover:bg-gray-100 transition-colors"
                    aria-label="Back to login"
                  >
                    <ArrowLeft className="w-4 h-4" />
                  </button>
                  <h2 className="text-xl font-bold text-[#1A1A2E]">Verify your identity</h2>
                </div>
                <p className="text-sm text-[#6B7280] mb-6">
                  Enter the 6-digit code sent to{' '}
                  <span className="font-medium text-[#1A1A2E]">{otpEmail}</span>
                </p>

                <form onSubmit={handleOtpSubmit} className="space-y-5">
                  <div>
                    <label className="block text-sm font-medium text-[#1A1A2E] mb-1.5">
                      Verification Code
                    </label>
                    <input
                      ref={otpInputRef}
                      type="text"
                      inputMode="numeric"
                      maxLength={6}
                      value={otpCode}
                      onChange={(e) => handleOtpInputChange(e.target.value)}
                      placeholder="000000"
                      required
                      autoComplete="one-time-code"
                      className="w-full px-4 py-3.5 rounded-xl border border-gray-200 text-center text-2xl font-mono tracking-[0.5em] text-[#1A1A2E] placeholder-[#D1D5DB] bg-[#F8FAF9] focus:outline-none focus:ring-2 focus:ring-primary-600/30 focus:border-primary-600 transition-colors"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={isLoading || otpCode.length !== 6}
                    className="w-full py-3 bg-primary-600 hover:bg-primary-700 text-white font-semibold rounded-xl transition-colors flex items-center justify-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed"
                  >
                    {isLoading ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Verifying...
                      </>
                    ) : (
                      'Verify & Sign In'
                    )}
                  </button>
                </form>

                {/* Resend OTP */}
                <div className="mt-5 text-center">
                  {resendCountdown > 0 ? (
                    <p className="text-sm text-[#6B7280]">
                      Resend code in{' '}
                      <span className="font-medium text-[#1A1A2E]">{resendCountdown}s</span>
                    </p>
                  ) : (
                    <button
                      type="button"
                      onClick={handleResendOtp}
                      className="text-sm font-medium text-primary-600 hover:text-primary-700 transition-colors"
                    >
                      Resend verification code
                    </button>
                  )}
                </div>
              </>
            )}
          </div>
        </div>

        <p className="text-center text-white/60 text-xs mt-6">
          &copy; 2026 SmartWasteCycle &middot; Admin Console v1.0
        </p>
      </div>
    </div>
  )
}

function RequirementItem({ met, text }: { met: boolean; text: string }) {
  return (
    <div className="flex items-center gap-2">
      <CheckCircle2 className={`w-3.5 h-3.5 flex-shrink-0 ${met ? 'text-green-500' : 'text-gray-300'}`} />
      <span className={`text-xs ${met ? 'text-green-700' : 'text-[#6B7280]'}`}>{text}</span>
    </div>
  )
}
